/**
 * El selector de tema.
 *
 * Tres opciones a la vista y no un boton que alterna: con un alternador no hay
 * forma de volver a "seguir al sistema" una vez que se toco, y quien trabaja de
 * dia y de noche quiere justamente eso.
 *
 * Es un grupo de radios de verdad, no botones: se recorre con las flechas, lo
 * anuncia el lector de pantalla como "claro, oscuro, sistema" y dice cual esta
 * elegido, todo sin JavaScript propio.
 *
 * Lo usan el panel y admin. Se apoya en las superficies de la app que lo monta
 * —`--surface-muted`, `--surface-raised`, `--border-subtle`, `.text-muted`— y
 * en la rampa `brand` de `@turnpro/tema`: los dos las tienen con esos mismos
 * nombres, asi que la pieza no trae colores propios.
 */

import { Monitor, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { useId } from "react";

import { cn } from "./cn";
import { useTema } from "./tema";
import type { Tema } from "./tema";

const OPCIONES: { valor: Tema; etiqueta: string; icono: typeof Sun }[] = [
  { valor: "claro", etiqueta: "Claro", icono: Sun },
  { valor: "oscuro", etiqueta: "Oscuro", icono: Moon },
  { valor: "sistema", etiqueta: "Sistema", icono: Monitor },
];

export function SelectorTema({
  /** Con `true` se ven las tres etiquetas; con `false`, solo los iconos. */
  conTexto = false,
  className,
}: {
  conTexto?: boolean;
  className?: string;
}): ReactNode {
  const { tema, cambiar } = useTema();
  const nombre = useId();

  return (
    <fieldset
      className={cn(
        "flex items-center gap-0.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-0.5",
        className,
      )}
    >
      <legend className="sr-only">Tema de la interfaz</legend>

      {OPCIONES.map((opcion) => {
        const Icono = opcion.icono;
        const elegido = tema === opcion.valor;

        return (
          <label
            key={opcion.valor}
            title={conTexto ? undefined : opcion.etiqueta}
            className={cn(
              "flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition",
              // El foco se dibuja en la etiqueta porque el radio esta oculto:
              // sin esto no se veria por donde va el teclado.
              "focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-500",
              elegido
                ? "bg-[var(--surface-raised)] font-medium shadow-sm"
                : "text-muted hover:text-[var(--text-strong)]",
            )}
          >
            <input
              type="radio"
              name={nombre}
              value={opcion.valor}
              checked={elegido}
              onChange={() => cambiar(opcion.valor)}
              className="sr-only"
            />
            <Icono className="size-4" aria-hidden="true" />
            <span className={conTexto ? undefined : "sr-only"}>{opcion.etiqueta}</span>
          </label>
        );
      })}
    </fieldset>
  );
}
